import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { DownloadSimple, Image as ImageIcon, Circle } from "@phosphor-icons/react";
import { fileUrl } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { rfqStatusMeta, fmtDateTime, RFQ_HISTORY_LABEL } from "@/lib/rfq";

export function RFQStatusBadge({ status }) {
  const meta = rfqStatusMeta(status);
  return (
    <Badge variant="outline" className={meta.className}>
      {meta.label}
    </Badge>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium break-words">{value || "—"}</p>
    </div>
  );
}

export function RFQInfoGrid({ rfq }) {
  if (!rfq) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
      <Field label="RFQ Number" value={rfq.rfq_number} />
      <Field label="Nama Karyawan" value={rfq.employee_name} />
      <Field label="Department" value={rfq.department} />
      <Field label="Nama PT" value={rfq.company_name} />
      <Field label="U.P" value={rfq.up_to} />
      <Field label="Tanggal Request" value={rfq.request_date} />
      <div className="col-span-2 sm:col-span-3">
        <Field label="Alamat PT" value={rfq.company_address} />
      </div>
      <Field label="Tanggal Dibutuhkan" value={rfq.required_date} />
      <div>
        <p className="text-xs text-muted-foreground">Status</p>
        <div className="mt-0.5"><RFQStatusBadge status={rfq.status} /></div>
      </div>
      <div className="col-span-2 sm:col-span-3">
        <Field label="Catatan" value={rfq.notes} />
      </div>
      {rfq.admin_notes && (
        <div className="col-span-2 sm:col-span-3 rounded-lg bg-accent/60 border border-border p-3">
          <p className="text-xs font-semibold text-accent-foreground mb-1">Admin Notes</p>
          <p className="text-sm whitespace-pre-wrap">{rfq.admin_notes}</p>
        </div>
      )}
    </div>
  );
}

export function RFQItemsTable({ items, onSelect }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">No</TableHead>
          <TableHead>Item</TableHead>
          <TableHead className="text-right">Quantity</TableHead>
          <TableHead>UOM</TableHead>
          <TableHead className="text-right">Harga</TableHead>
          <TableHead className="text-right">Total</TableHead>
          <TableHead>Gambar</TableHead>
          <TableHead>Keterangan Tambahan</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {(items || []).map((it, i) => (
          <TableRow
            key={i}
            className={onSelect ? "cursor-pointer" : undefined}
            onClick={onSelect ? () => onSelect(it, i) : undefined}
            data-testid={onSelect ? `rfq-detail-item-${i}` : undefined}
          >
            <TableCell>{i + 1}</TableCell>
            <TableCell>
              <p className="font-medium">{it.item_name}</p>
              {it.specification && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{it.specification}</p>}
            </TableCell>
            <TableCell className="text-right tabular-nums">{it.qty}</TableCell>
            <TableCell>{it.uom}</TableCell>
            <TableCell className="text-right tabular-nums">{formatRupiah(it.price)}</TableCell>
            <TableCell className="text-right tabular-nums font-semibold">{formatRupiah(it.total)}</TableCell>
            <TableCell>
              {it.image_path ? (
                <a
                  href={fileUrl(it.image_path)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary hover:underline inline-flex items-center gap-1 text-sm"
                >
                  <ImageIcon size={16} /> Lihat
                </a>
              ) : (
                <span className="text-muted-foreground text-xs">—</span>
              )}
            </TableCell>
            <TableCell className="max-w-[180px] truncate text-sm">{it.description || "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function RFQItemDetailModal({ item, index, onClose }) {
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="rfq-item-detail-modal">
        <DialogHeader>
          <DialogTitle>Detail Item #{(index ?? 0) + 1}</DialogTitle>
        </DialogHeader>
        {item && (
          <div className="space-y-4">
            <Field label="Item" value={item.item_name} />
            {item.specification && (
              <div>
                <p className="text-xs text-muted-foreground">Spesifikasi</p>
                <p className="text-sm whitespace-pre-wrap break-words">{item.specification}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Quantity" value={String(item.qty ?? "—")} />
              <Field label="UOM" value={item.uom} />
              <Field label="Harga per Unit" value={formatRupiah(item.price)} />
              <Field label="Total" value={formatRupiah(item.total)} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Gambar</p>
              {item.image_path ? (
                <a href={fileUrl(item.image_path)} target="_blank" rel="noreferrer">
                  <img
                    src={fileUrl(item.image_path)}
                    alt={item.item_name}
                    className="w-full max-h-64 object-contain rounded-lg border border-border bg-muted"
                  />
                </a>
              ) : (
                <p className="text-sm text-muted-foreground">Tidak ada gambar.</p>
              )}
            </div>
            <Field label="Keterangan Tambahan" value={item.description} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RFQQuotationBox({ rfq, onDownload, downloading }) {
  const quo = rfq?.quotation;
  if (!quo) return null;
  return (
    <div className="rounded-xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5 p-4 space-y-2">
      <h4 className="font-heading font-bold">Quotation Result</h4>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span><span className="text-muted-foreground">File: </span><span className="font-medium">{quo.file_name}</span></span>
        <span><span className="text-muted-foreground">Tanggal upload: </span>{fmtDateTime(quo.uploaded_at)}</span>
        <span><span className="text-muted-foreground">Uploaded by: </span>{quo.uploaded_by}</span>
      </div>
      <Button onClick={onDownload} disabled={downloading} className="gap-2" data-testid="download-quotation-button">
        <DownloadSimple size={18} weight="bold" /> {downloading ? "Menyiapkan…" : "Download Quotation"}
      </Button>
    </div>
  );
}

export function RFQTimeline({ history }) {
  if (!history || history.length === 0) return null;
  return (
    <div className="space-y-1">
      <h4 className="font-heading font-bold mb-2">Activity / Timeline</h4>
      <div className="space-y-3">
        {history.map((h, i) => (
          <div key={i} className="flex gap-3 text-sm">
            <Circle size={12} weight="fill" className="mt-1 shrink-0 text-primary" />
            <div>
              <p className="font-medium">{RFQ_HISTORY_LABEL[h.action] || h.action}</p>
              <p className="text-sm text-muted-foreground">{h.detail}</p>
              <p className="text-xs text-muted-foreground">{fmtDateTime(h.at)} · {h.by_name}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
